import { useCallback, useState } from 'react'

/**
 * Filtro de lista que sobrevive à navegação (docs/styleguide/03-componentes.md · 08): guardado em
 * `sessionStorage` sob `wx-<tela>-filter`. Devolve também se a pessoa já escolheu alguma vez, para
 * a tela abrir no segmento que tem pendência quando ainda não houve escolha.
 */
export function useStoredFilter<T extends string>(
  screen: string,
  allowed: readonly T[],
  fallback: T,
) {
  const key = `wx-${screen}-filter`
  const read = (): T | null => {
    try {
      const raw = window.sessionStorage.getItem(key)
      return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : null
    } catch {
      return null
    }
  }
  const [stored, setStored] = useState<T | null>(read)
  const setFilter = useCallback(
    (value: T) => {
      setStored(value)
      try {
        window.sessionStorage.setItem(key, value)
      } catch {
        /* sem storage */
      }
    },
    [key],
  )
  return { filter: stored ?? fallback, hasChosen: stored !== null, setFilter }
}

/**
 * Apaga as escolhas de filtro guardadas (`wx-<tela>-filter`). Ao trocar de clínica e ao sair, "listas
 * e filtros recomeçam" (docs/areas/autenticacao.md, "Troca de clínica dentro da sessão").
 */
export function clearStoredFilters() {
  try {
    const storage = window.sessionStorage
    const keys: string[] = []
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index)
      if (key && key.startsWith('wx-') && key.endsWith('-filter')) keys.push(key)
    }
    for (const key of keys) storage.removeItem(key)
  } catch {
    // Sem armazenamento, não há o que apagar.
  }
}
